import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search, QrCode, Loader2 } from "lucide-react";

export default function AdminQRCodes() {
  const [qrCodes, setQrCodes] = useState([]);
  const [type, setType] = useState("");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 50 });
      if (type) params.set("type", type);
      const { data } = await API.get(`/admin/qr-codes?${params}`);
      setQrCodes(data.qr_codes || []);
      setTotalPages(data.pages || 1);
      setTotal(data.total || 0);
    } catch {} finally { setLoading(false); }
  }, [type, page]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="admin-qr-codes">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>QR Codes ({total})</h2>
      </div>

      <div className="flex gap-3">
        <select data-testid="qr-type-filter" value={type} onChange={(e) => { setType(e.target.value); setPage(1); }} className="h-10 rounded-xl border border-slate-200 px-3 text-sm bg-white">
          <option value="">All Types</option>
          <option value="manufacturing">Manufacturing</option>
          <option value="traded">Traded</option>
        </select>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#008A99]" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-slate-700">QR String</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-700">Product</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-700">Batch</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-700">Type</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-700">Status</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-700">Mfg / Exp</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {qrCodes.map((qr, i) => (
                  <tr key={i} className="hover:bg-slate-50/50" data-testid={`qr-row-${i}`}>
                    <td className="px-4 py-3 font-mono text-xs text-slate-600 max-w-[200px] truncate">{qr.qr_string}</td>
                    <td className="px-4 py-3 font-medium">{qr.product_code}</td>
                    <td className="px-4 py-3 text-slate-500">{qr.batch_number}</td>
                    <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full ${qr.type === "manufacturing" ? "bg-[#E0F4F5] text-purple-600" : "bg-amber-50 text-amber-600"}`}>{qr.type}</span></td>
                    <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full ${qr.status === "active" ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500"}`}>{qr.status}</span></td>
                    <td className="px-4 py-3 text-xs text-slate-400">{qr.mfg_date} / {qr.exp_date}</td>
                  </tr>
                ))}
                {qrCodes.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-400">No QR codes found</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-4 py-2 text-sm rounded-xl border disabled:opacity-40">Prev</button>
          <span className="px-4 py-2 text-sm">{page} / {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-4 py-2 text-sm rounded-xl border disabled:opacity-40">Next</button>
        </div>
      )}
    </div>
  );
}
